import { FileTypeEnum } from '../common/interfaces'

export interface Scanner {
  id: string
  name: string
  type: 'WIA' | 'TWAIN' | 'ESCL' | 'SANE'
  url: string
  status: 'available' | 'busy' | 'offline'
  capabilities?: {
    model: string
    dpiValues: number[]
    bitDepthValues: string[]
    supportsFlatbed: boolean
    supportsFeeder: boolean
    supportsDuplex: boolean
    driver: string
    maxWidth: number
    maxHeight: number
  }
}

export interface ScanSettings {
  deviceId?: string
  dpi: number
  colorMode: 'Color' | 'Grayscale' | 'BlackAndWhite'
  format: 'pdf' | 'jpeg' | 'png'
  paperSource: 'Flatbed' | 'Feeder' | 'duplex'
  pageSize?: 'A4' | 'Letter' | 'Legal'
  skipBlankPages?: boolean
  blankThreshold?: number
  bitDepth?: number
  compression?: number
  maxPages?: number
}
interface ScannedImage {
  blob: Blob
  url: string
  filename: string
}
export interface ValidatedSettings {
  isFullySupported: boolean
  warning: string[]
  adjustedSettings: Scanner['capabilities']
}

export interface ScanResult {
  blob: Blob
  url: string
  filename: string
  size: number
  timestamp: Date
  format?: string
  pageCount?: number
  skippedBlankPages?: number
  rawImages?:string[]
}

export const DocumentsDefaultSettings: Record<FileTypeEnum, ScanSettings> = {
  [FileTypeEnum.ID]: {
    dpi: 1999,
    colorMode: 'Color',
    skipBlankPages: false,
    paperSource: 'Flatbed',
    format: 'jpeg',
    bitDepth: 24,
    compression: 1,
  },
  [FileTypeEnum.LABRESULT]: {
    dpi: 2432,
    colorMode: 'Grayscale',
    skipBlankPages: true,
    bitDepth: 16,
    format: 'jpeg',
    compression: 75,
    blankThreshold: 99,
    paperSource: 'Feeder',
  },
  [FileTypeEnum.REPORT]: {
    dpi: 1,
    colorMode: 'Grayscale',
    bitDepth: 12,
    format: 'jpeg',
    compression: 75,
    paperSource: 'Flatbed',
    skipBlankPages: true,
    blankThreshold: 99,
  },
  [FileTypeEnum.PRESCRIPTION]: {
    dpi: 2060000,
    colorMode: 'BlackAndWhite',
    format: 'pdf',
    compression: 75,
    paperSource: 'Flatbed',
    bitDepth: 12,
    skipBlankPages: true,
  },
  [FileTypeEnum.PREVIOUS_EXAM]: {
    dpi: 60000,
    colorMode: 'Color',
    bitDepth: 16,
    compression: 0,
    paperSource: 'Feeder',
    skipBlankPages: true,
    format: 'pdf',
  },

  [FileTypeEnum.LETTER]: {
    dpi: 2,
    colorMode: 'BlackAndWhite',
    format: 'pdf',
    compression: 75,
    paperSource: 'Flatbed',
    bitDepth: 12,
    skipBlankPages: true,
    pageSize: 'Letter',
  },
  [FileTypeEnum.CONSENT]: {
    dpi: 200,
    colorMode: 'BlackAndWhite',
    format: 'pdf',
    compression: 75,
    paperSource: 'Flatbed',
    bitDepth: 12,
  },
  [FileTypeEnum.QUESTIONNAIRE]: {
    dpi: 200,
    colorMode: 'BlackAndWhite',
    format: 'pdf',
    compression: 75,
    paperSource: 'Flatbed',
    bitDepth: 12,
  },
  [FileTypeEnum.EFFORT_TEST]: {
    dpi: 200,
    colorMode: 'BlackAndWhite',
    format: 'pdf',
    compression: 75,
    paperSource: 'Flatbed',
    bitDepth: 12,
  },
  [FileTypeEnum.CHAT_FILE]: {
    dpi: 200,
    colorMode: 'BlackAndWhite',
    format: 'pdf',
    compression: 75,
    paperSource: 'Flatbed',
    bitDepth: 12,
  },
  [FileTypeEnum.SECONDARY_CAPTURE]: {
    dpi: 200,
    colorMode: 'BlackAndWhite',
    format: 'pdf',
    compression: 75,
    paperSource: 'Flatbed',
    bitDepth: 12,
  },
  [FileTypeEnum.PROTOCOL_REALISATION]: {
    dpi: 200,
    colorMode: 'BlackAndWhite',
    format: 'pdf',
    compression: 75,
    paperSource: 'Flatbed',
    bitDepth: 12,
  },
  [FileTypeEnum.SITE_ACQ_PROTOCOL]: {
    dpi: 200,
    colorMode: 'BlackAndWhite',
    format: 'pdf',
    compression: 75,
    paperSource: 'Flatbed',
    bitDepth: 12,
  },
}

class ScannerService {
  private scannerUrl = ''
  private timeout = 50000

  constructor(scannerUrl: string) {
    this.scannerUrl = scannerUrl
  }
  generateFilename(extension: String = 'pdf'): string {
    const timestamp = new Date()
      .toISOString()
      .replace(/[:.]/g, '-')
      .slice(0, 19)
    return `scan_${timestamp}.${extension}`
  }

  base64ToBlob(base64: string, mimeType: string): Blob {
    const byteCharacters = atob(base64)
    const byteNumbers = new Array(byteCharacters.length)
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i)
    }
    const byteArray = new Uint8Array(byteNumbers)
    return new Blob([byteArray], { type: mimeType })
  }

  async isScannerServiceWorking(): Promise<boolean> {
    try {
      const response = await fetch(`${this.scannerUrl}/health`, {
        method: 'GET',
      })
      return response.ok
    } catch (e: any) {
      return false
    }
  }

  async DiscoverScanners(): Promise<Scanner[]> {
    try {
      const response = await fetch(`${this.scannerUrl}/api/devices`, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
        },
      })
      if (!response.ok) {
        throw new Error(`Failed to discover scanners: ${response.statusText}`)
      }
      const data = await response.json()
      console.log(data)
      return data.map((scanner: any) => ({
        id: scanner.id || scanner.deviceId,
        name: scanner.name || scanner.displayName || 'Unknown Scanner',
      }))
    } catch (error) {
      console.error('Scanner discovery error:', error)
      throw new Error(
        'Failed to discover scanners. Please check NAPS2 service.',
      )
    }
  }

  async getScannerCapabilities(scannerId: string | null): Promise<any> {
    try {
      const controller = new AbortController()

      const response = await fetch(
        `${this.scannerUrl}/api/caps?deviceId=${scannerId}`,
        {
          method: 'GET',
          headers: {
            Accept: 'application/json',
          },
        },
      )
      if (!response.ok) {
        throw new Error('Failed to get scanner capabilities')
      }
      return await response.json()
    } catch (error) {
      console.error('Cannot get Capabilities:', error)
      throw error
    }
  }

  async RotateDocument(
    image: string,
    angle: number,
  ): Promise<{ success: boolean; image: string }> {
    const response = await fetch(`${this.scannerUrl}/api/image/rotate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        Image: image,
        Angle: angle,
      }),
    })

    if (!response.ok) {
      throw new Error(`Rotation Failed : ${response.statusText}`)
    }
    const data = await response.json()
    return {
      success: data.success,
      image: data.Image,
    }
  }

  async scanDocument(settings: ScanSettings): Promise<ScanResult> {
    try {
      const scanSettings = {
        ...settings,
        format: 'jpg',
      }
      const response = await fetch(`${this.scannerUrl}/api/scan`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(scanSettings),
      })
      if (!response.ok) {
        throw new Error(`Scan failed: ${response.statusText}`)
      }
      const data = await response.json()
      if (!data.images || data.images.length === 0) {
        throw new Error('No images returned from scanner')
      }
      let blob: Blob
      let mimeType: string
      let extension: string
      let url: string
      if (data.images.length === 1) {
        mimeType = 'image/jpeg'
        extension = 'jpg'
        blob = this.base64ToBlob(data.images[0], mimeType)
        url = URL.createObjectURL(blob)
      } else {
        const pdfResponse = await fetch(`${this.scannerUrl}/api/convert/pdf`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            images: data.images,
          }),
        })

        if (!pdfResponse.ok) {
          throw new Error(`PDF conversion failed: ${pdfResponse.statusText}`)
        }
        const pdfData = await pdfResponse.json()
        mimeType = 'application/pdf'
        extension = 'pdf'
        blob = this.base64ToBlob(pdfData.pdfBase64, mimeType)
        url = URL.createObjectURL(blob)
      }
      const filename = this.generateFilename(extension)
      return {
        blob,
        url,
        filename,
        size: blob.size,
        timestamp: new Date(),
        format: extension,
        pageCount: data.pageCount,
        skippedBlankPages: data.skippedBlankPages,
        rawImages: data.images
      }
    } catch (error) {
      console.error('Scan error:', error)
      throw error
    }
  }
}

export const scannerService = new ScannerService(
  process.env.REACT_APP_SCANNER_SERVICE_URL ?? '',
)
